"use client";

import {
  type FC,
  type MutableRefObject,
  useEffect,
  useId,
  useRef,
} from "react";
import { cn } from "utils";
import { ETF_OVERLAP_GUIDE_COPY as COPY } from "../../constants/copy";
import { overlapAttributionDataProps } from "../../tracking/overlapAttribution";
import type { OverlapEntrySource } from "../../types";
import {
  type GuideCloseMethod,
  useGuideCloseMethod,
} from "./useGuideCloseMethod";

export type OverlapGuideAction = "connect" | "upload";

export interface OverlapGuideContentProps {
  entrySource: OverlapEntrySource;
  onAction: (action: OverlapGuideAction) => void;
  /** opener 用它在 `onDismiss` 里读取关闭方式；内容挂载后写入 */
  closeMethodRef?: MutableRefObject<() => GuideCloseMethod>;
}

/**
 * SC-04 引导弹窗内容（EDG-561）。Figma：Mobile 109896:81919 · Desktop 109921:531343。
 * H5 底部抽屉 / PC 居中 Popup 仅换壳（见 openEtfOverlapGuideSheet）；Grabber 放在内容内，仅手机显示。
 * 示例卡与预览区只是示意，不可点。
 */
export const OverlapGuideContent: FC<OverlapGuideContentProps> = ({
  entrySource,
  onAction,
  closeMethodRef,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const getCloseMethod = useGuideCloseMethod(rootRef);

  useEffect(() => {
    if (closeMethodRef) {
      closeMethodRef.current = getCloseMethod;
    }
  }, [closeMethodRef, getCloseMethod]);

  // 归因四字段（ad_source / platform / …）整体展开；`entry_source` 在每个元素上写成字面量，
  // 否则 lint-or-params 的静态扫描看不到 `data-or-param-*`
  const attributionProps = overlapAttributionDataProps();

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="w-full bg-background_solid_secondary pb-8.5 md:pb-0"
      data-testid="overlap-guide"
      data-or-view="overlap_guide_view"
      data-or-param-entry_source={entrySource}
      {...attributionProps}
    >
      <div
        className="mx-auto mt-2.5 h-1 w-9 rounded-0.5 bg-border_inverse_transparent10 md:hidden"
        data-testid="overlap-guide-grabber"
      />

      <div
        className="flex flex-col items-center gap-3 bg-background_brand_overlay10 px-6 py-8"
        data-testid="overlap-guide-preview"
      >
        <div className="flex w-full flex-col gap-2.5 rounded-4 bg-background_solid_primary p-5">
          <span className="body_medium_s text-content_tertiary">
            {COPY.exampleLabel}
          </span>
          <div className="flex items-baseline gap-2">
            <span className="title_xxxl text-content_brand_primary">
              {COPY.exampleValue}
            </span>
            <span className="body_l text-content_secondary">
              {COPY.exampleSuffix}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-4 pt-6 md:px-8 md:pb-8">
        <div className="flex flex-col gap-2 text-center">
          <h2
            id={titleId}
            className="headline_regular_m text-center text-content_primary"
          >
            {COPY.title}
          </h2>
          <p className="body_l text-center text-content_secondary">
            {COPY.body}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => onAction("connect")}
            data-openreplay-label="overlap_guide_connect_cta"
            data-or-event="overlap_guide_connect_tap"
            data-or-param-entry_source={entrySource}
            {...attributionProps}
            className={cn("button", "md_button", "primary_button", "button_m")}
          >
            {COPY.connect}
          </button>
          <button
            type="button"
            onClick={() => onAction("upload")}
            data-openreplay-label="overlap_guide_upload_cta"
            data-or-event="overlap_guide_upload_tap"
            data-or-param-entry_source={entrySource}
            {...attributionProps}
            className={cn(
              "button",
              "md_button",
              "text_button",
              "button_m",
              "text-content_primary",
            )}
          >
            {COPY.upload}
          </button>
        </div>
      </div>
    </div>
  );
};
